import {
  Backdrop, CircularProgress, Tooltip, Typography, Box,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Stack, TextField, Switch, FormControl, FormLabel, IconButton, Autocomplete,
  Button
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MDButton from "components/MDButton";
import DataTable from "comps/Tables/DataTable";
import React, { useEffect, useMemo, useState } from "react";
import TLinkApi from "services/tlink.api";
import MRTClientDataTable from "comps/Tables/DataTable/MRTClientDataTable";
import { MRT_ShowHideColumnsButton, MRT_ToggleFiltersButton, MRT_ToggleFullScreenButton } from "material-react-table";

const BASE_ENDPOINT = "/configurations/supplier-client-priority";
const LANG_ENDPOINT = "/masters/language";

const oneLine = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

const ClientConfigurations = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [data, setData] = useState([]);

  // language catalog
  const [languages, setLanguages] = useState([]);               // [{id, name}]
  const [languageMap, setLanguageMap] = useState({});           // {id: name}

  // modal + form
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState("add"); // 'add' | 'edit'
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // form fields
  const [supplierId, setSupplierId] = useState("");
  const [buyerId, setBuyerId] = useState(""); // optional
  const [clientId, setClientId] = useState("");
  const [isClientAllowed, setIsClientAllowed] = useState(false);
  // const [allowedLanguageIds, setAllowedLanguageIds] = useState([]);
  const [allowedLanguageId, setAllowedLanguageId] = useState(-1);
  const [allowedBuyers, setAllowedBuyers] = useState("");
  const [priorityOrder, setPriorityOrder] = useState("");          // 1..10 only
  const [apiSupplier, setApiSupplier] = useState(false);
  const [isSupplierActive, setIsSupplierActive] = useState(false);
  const [isClientActive, setIsClientActive] = useState(false);

  // id for PUT url/${id}
  const [editingId, setEditingId] = useState(null);

  // --- helpers ---
  const fetchLanguages = async () => {
    try {
      const res = await TLinkApi.get(LANG_ENDPOINT);
      const list = (res?.data ?? [])
        .map((l) => ({ id: Number(l.id), name: String(l.name) }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setLanguages(list);
      setLanguageMap(Object.fromEntries(list.map((l) => [l.id, l.name])));
    } catch (e) {
      console.log(e);
    }
  };

  const languageOptions = useMemo(
    () => [{ id: -1, name: "All Languages" }, ...languages],
    [languages]
  );

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await TLinkApi.get(BASE_ENDPOINT);
      const rows = (res?.data ?? []).map((r) => ({
        id: r.id ?? r.priority_id ?? null,
        supplier_id: r.supplier_id,
        supplier_name: r.supplier_name,
        buyer_id: r.buyer_id,
        buyer_name: r.buyer_name,
        client_id: r.client_id,
        client_name: r.client_name,
        is_client_allowed: !!r.is_client_allowed,
        allowed_buyers: r.allowed_buyers ?? "",
        allowed_languages: r.allowed_languages ?? "",
        priority_order: Number.isFinite(+r.priority_order) ? Number(r.priority_order) : null,
        api_supplier: !!r.api_supplier,
        is_supplier_active: !!r.is_supplier_active,
        is_client_active: !!r.is_client_active,
      }));
      setData(rows);
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLanguages();
    fetchData();
  }, []);

  const parseAllowedLanguageId = (raw) => {
    // accept -1 / "All Languages" / "All Lanaguages" / numeric id / language name
    if (raw === -1 || raw === "-1") return -1;
    if (typeof raw === "string") {
      const s = raw.trim();
      if (/^all\s+lan?guages?$/i.test(s)) return -1; // tolerate the "Lanaguages" typo too
      const asNum = Number(s);
      if (Number.isFinite(asNum)) return asNum;
      // try by name
      const byName = languages.find(l => l.name.toLowerCase() === s.toLowerCase());
      if (byName) return byName.id;
    }
    if (Number.isFinite(raw)) return Number(raw);
    return -1; // default to All if unknown
  };

  const openAdd = () => {
    setMode("add");
    setEditingId(null);
    setSupplierId("");
    setBuyerId("");
    setClientId("");
    setIsClientAllowed(false);
    setAllowedLanguageId(-1);
    setAllowedBuyers("");
    setPriorityOrder("");
    setApiSupplier(false);
    setIsSupplierActive(false);
    setIsClientActive(false);
    setErrorMsg("");
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setMode("edit");
    setEditingId(row.id ?? null);
    setSupplierId(String(row.supplier_id ?? ""));
    setBuyerId(row.buyer_id != null ? String(row.buyer_id) : "");
    setClientId(String(row.client_id ?? ""));
    setIsClientAllowed(Boolean(row.is_client_allowed));
    setAllowedLanguageId(parseAllowedLanguageId(row.allowed_languages));
    setAllowedBuyers(row.allowed_buyers ?? "");
    setPriorityOrder(row.priority_order != null ? String(row.priority_order) : "");
    setApiSupplier(Boolean(row.api_supplier));
    setIsSupplierActive(Boolean(row.is_supplier_active));
    setIsClientActive(Boolean(row.is_client_active));
    setErrorMsg("");
    setModalOpen(true);
  };

  const onPriorityChange = (e) => {
    const raw = e.target.value.replace(/[^\d]/g, "");
    if (raw === "") return setPriorityOrder("");
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 1 || n > 10) return;
    setPriorityOrder(raw);
  };

  const valid =
    supplierId.trim() !== "" &&
    clientId.trim() !== "" &&
    priorityOrder.trim() !== "" &&
    Number(priorityOrder) >= 1 &&
    Number(priorityOrder) <= 10;

  // duplicate guard (supplier, client, buyer)
  const tripleToRowId = useMemo(() => {
    const m = new Map();
    data.forEach((r) => m.set(`${r.supplier_id}:${r.client_id}:${r.buyer_id ?? ""}`, r.id ?? null));
    return m;
  }, [data]);

  const handleSave = async () => {
    if (!valid) {
      setErrorMsg("Supplier ID, Client ID and Priority Order (1–10) are required.");
      return;
    }

    const key = `${Number(supplierId)}:${Number(clientId)}:${buyerId.trim() ? Number(buyerId) : ""}`;
    const existingRowId = tripleToRowId.get(key);
    if (mode === "add" && existingRowId != null) {
      setErrorMsg("A rule for this Supplier/Client/Buyer already exists.");
      return;
    }
    if (mode === "edit" && existingRowId != null && existingRowId !== editingId) {
      setErrorMsg("Another record already uses this Supplier/Client/Buyer.");
      return;
    }

    setSubmitting(true);
    setErrorMsg("");
    try {
      const payload = {
        supplierId: Number(supplierId),
        buyerId: buyerId.trim() ? Number(buyerId) : null,
        clientId: Number(clientId),
        isClientAllowed: Boolean(isClientAllowed),
        allowedBuyers: allowedBuyers || null,
        languageId: Number(allowedLanguageId),
        priorityOrder: Number(priorityOrder),
        apiSupplier: Boolean(apiSupplier),
        isSupplierActive: Boolean(isSupplierActive),
        isClientActive: Boolean(isClientActive),
      };

      if (mode === "add") {
        await TLinkApi.post(BASE_ENDPOINT, payload);
      } else {
        if (!editingId) {
          setErrorMsg("Missing row id for update.");
          setSubmitting(false);
          return;
        }
        await TLinkApi.update(`${BASE_ENDPOINT}/${editingId}`, payload);
      }

      setModalOpen(false);
      await fetchData();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Something went wrong";
      setErrorMsg(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessor: "supplier_id",
        Header: "Supplier Id",
        align: "left",
        Cell: ({ row }) => {
          const v = row?.original?.supplier_id;
          return <div>{(v === null || v === undefined || v === "") ? "-" : v}</div>;
        },
      },
      {
        accessor: "supplier_name",
        Header: "Supplier Name",
        align: "left",
        sortable: true,
        Cell: ({ row }) => {
          // const name = row?.original?.supplier_name;
          // if (!name) return "";
          // const truncated = name.length > 20 ? name.substring(0, 20) + "..." : name;
          const name = row?.original?.supplier_name || "";
          const truncated = name
            ? (name.length > 20 ? name.substring(0, 20) + "..." : name)
            : "-";
          return (
            <Tooltip title={name && name.length > 20 ? name : ""}>
              <div style={oneLine}>{truncated}</div>
            </Tooltip>
          );
        },
      },
      { accessor: "buyer_id", Header: "Buyer Id", align: "left", Cell: ({ row }) => { const v = row?.original?.buyer_id; return <div>{(v === null || v === undefined || v === "") ? "-" : v}</div>; }, },
      {
        accessor: "buyer_name",
        Header: "Buyer Name",
        align: "left",
        sortable: true,
        Cell: ({ row }) => {
          const name = row?.original?.buyer_name || "";
          const truncated = name
            ? (name.length > 20 ? name.substring(0, 20) + "..." : name)
            : "-";
          return (
            <Tooltip title={name && name.length > 20 ? name : ""}>
              <div style={oneLine}>{truncated}</div>
            </Tooltip>
          );
        },
      },
      {
        accessor: "client_id",
        Header: "Client Id",
        align: "left",
        Cell: ({ row }) => {
          const v = row?.original?.client_id;
          return <div>{(v === null || v === undefined || v === "") ? "-" : v}</div>;
        },
      },
      {
        accessor: "client_name",
        Header: "Client Name",
        align: "left",
        sortable: true,
        Cell: ({ row }) => {
          const name = row?.original?.client_name || "";
          const truncated = name
            ? (name.length > 20 ? name.substring(0, 20) + "..." : name)
            : "-";
          return (
            <Tooltip title={name && name.length > 20 ? name : ""}>
              <div style={oneLine}>{truncated}</div>
            </Tooltip>
          );
        },
      },
      {
        accessor: "is_client_allowed",
        Header: "Client Allowed",
        align: "center",
        headerAlign: "center",
        Cell: ({ row }) => (row.original.is_client_allowed ? "Yes" : "No"),
      },
      {
        accessor: "allowed_languages",
        Header: "Allowed Languages",
        align: "center",
        headerAlign: "center",
        Cell: ({ row }) => {
          const id = parseAllowedLanguageId(row.original.allowed_languages);
          const display = id === -1 ? "All Languages" : (languageMap[id] ?? String(id)) || "-";
          return (
            <Tooltip title={display}>
              <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {display}
              </div>
            </Tooltip>
          );
        },
      },
      {
        accessor: "allowed_buyers",
        Header: "Allowed Buyers",
        align: "center",
        headerAlign: "center",
        Cell: ({ row }) => {
          const txt = row.original.allowed_buyers || "-";
          return (
            <Tooltip title={txt}>
              <div style={oneLine}>{txt}</div>
            </Tooltip>
          );
        },
      },
      {
        accessor: "priority_order",
        Header: "Priority Order",
        align: "center",
        headerAlign: "center",
        Cell: ({ row }) => {
          const v = row?.original?.priority_order;
          return <div>{(v === null || v === undefined || v === "") ? "-" : v}</div>;
        },
      },
      {
        accessor: "api_supplier",
        Header: "API Supplier",
        align: "center",
        headerAlign: "center",
        Cell: ({ row }) => (row.original.api_supplier ? "Yes" : "No"),
      },
      {
        accessor: "is_supplier_active",
        Header: "Supplier Active",
        align: "center",
        headerAlign: "center",
        Cell: ({ row }) => (row.original.is_supplier_active ? "Yes" : "No"),
      },
      {
        accessor: "is_client_active",
        Header: "Client Active",
        align: "center",
        headerAlign: "center",
        Cell: ({ row }) => (row.original.is_client_active ? "Yes" : "No"),
      },
      {
        accessor: "actions",
        Header: "Actions",
        align: "right",
        headerAlign: "right",
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1} justifyContent="flex-end" pr={1}>
            <IconButton size="small" onClick={() => openEdit(row.original)} sx={{ p: 0.5 }}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [languageMap]
  );

  const [fullScr, setFullScr] = useState(false);
  const renderToolbarInternalActions = ({ table }) => {
    return (
      <>
        <div style={{ display: "flex", alignItems: "center", gap: "0rem" }}>
          <Button variant="contained" color="info" onClick={openAdd}>
            + New
          </Button>
          <MRT_ShowHideColumnsButton table={table} />
          <MRT_ToggleFiltersButton table={table} />
          <div onClick={() => setFullScr(!fullScr)} >
            <MRT_ToggleFullScreenButton table={table} />
          </div>
        </div>
      </>
    );
  };
  return (
    <div>
      {isLoading ? (
        <Backdrop sx={{ color: "#fff", zIndex: (t) => t.zIndex.drawer + 1 }} open>
          <CircularProgress color="inherit" />
        </Backdrop>
      ) : null}

      {/* <DataTable
        table={{ columns, rows: data }}
        isSorted
        entriesPerPage={entriesPerPage}
        canSearch={false}
        tablePage={page}
        setTablePage={setPage}
        setEntriesPerPage={setEntriesPerPage}
        totalLength={data.length}
        pagination
        enableTopToolbar
        tableMaxHeight={data.length === 0 ? "calc(100vh - 220px)" : "calc(100vh - 220px)"}
        tableMinHeight={data.length === 0 ? "calc(100vh - 220px)" : "calc(100vh - 220px)"}
        renderTopToolbarCustomActions={() => (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: window.innerWidth > 1600 ? "calc(76vw - 580px)" : "calc(76vw - 565px)", marginTop: "3px" }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "black" }}>
              Client Priority Configuration
            </Typography>
            <Button variant="contained" color="info" onClick={openAdd}>
              + New
            </Button>
          </Box>
        )}
      /> */}

      <MRTClientDataTable
        table={{ columns, rows: data }}
        name="Client Priority Configuration"
        canSearch={false}                 // or true if you want the search box
        enableTopToolbar
        defaultPageSize={50}
        entriesOption={["10", "20", "30", "40", "50"]}
        tableMaxHeight={"calc(100vh - 220px)"}
        tableMinHeight={"calc(100vh - 220px)"}
        navigateRow={null}                // or "/path" if you want row navigation
        initialState={{
          // optional: set initial sorting, etc.
          sorting: [{ id: "supplier_name", desc: false }],
        }}
        renderToolbarInternalActions={renderToolbarInternalActions}
        fullScr={fullScr}
        renderTopToolbarCustomActions={() => (
          <Box
          // sx={{
          //   display: "flex",
          //   justifyContent: "space-between",
          //   alignItems: "center",
          //   width:
          //     window.innerWidth > 1600
          //       ? "calc(76vw - 580px)"
          //       : "calc(76vw - 565px)",
          //   mt: "3px",
          // }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: "black" }}>
              Client Priority Configuration
            </Typography>
            {/* <Button variant="contained" color="info" onClick={openAdd}>
              + New
            </Button> */}
          </Box>
        )}
      />

      {/* Add / Edit Modal */}
      <Dialog open={modalOpen} onClose={() => !submitting && setModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{mode === "add" ? "Add" : "Update"}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>Supplier ID</FormLabel>
                <TextField size="small" type="number" value={supplierId} onChange={(e) => setSupplierId(e.target.value)} />
              </FormControl>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>Buyer ID (optional)</FormLabel>
                <TextField size="small" type="number" value={buyerId} onChange={(e) => setBuyerId(e.target.value)} />
              </FormControl>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>Client ID</FormLabel>
                <TextField size="small" type="number" value={clientId} onChange={(e) => setClientId(e.target.value)} />
              </FormControl>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              {/* <FormControl fullWidth>
                <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>Allowed Buyers</FormLabel>
                <TextField
                  size="small"
                  placeholder=''
                  value={allowedBuyers}
                  onChange={(e) => setAllowedBuyers(e.target.value)}
                />
              </FormControl> */}

              <FormControl fullWidth>
                <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>Allowed Languages</FormLabel>
                <Autocomplete
                  options={languageOptions}
                  value={languageOptions.find(l => l.id === allowedLanguageId) ?? languageOptions[0]}
                  onChange={(_, v) => setAllowedLanguageId(v?.id ?? -1)}
                  getOptionLabel={(opt) => opt?.name ?? ""}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  renderInput={(params) => (
                    <TextField {...params} size="small" placeholder="Select a language" />
                  )}
                />
              </FormControl>

              <FormControl fullWidth>
                <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>Priority Order (1–10)</FormLabel>
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 1, max: 10, step: 1 }}
                  value={priorityOrder}
                  onChange={onPriorityChange}
                  placeholder="1–10"
                  helperText="Choose a whole number between 1 and 10"
                />
              </FormControl>
            </Stack>

            {/* <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControl>
                <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>Client Allowed</FormLabel>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Switch checked={isClientAllowed} onChange={(e) => setIsClientAllowed(e.target.checked)} />
                  <Typography variant="body2">{isClientAllowed ? "Yes" : "No"}</Typography>
                </Stack>
              </FormControl>

              <FormControl>
                <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>API Supplier</FormLabel>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Switch checked={apiSupplier} onChange={(e) => setApiSupplier(e.target.checked)} />
                  <Typography variant="body2">{apiSupplier ? "Yes" : "No"}</Typography>
                </Stack>
              </FormControl>

              <FormControl>
                <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>Supplier Active</FormLabel>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Switch checked={isSupplierActive} onChange={(e) => setIsSupplierActive(e.target.checked)} />
                  <Typography variant="body2">{isSupplierActive ? "Yes" : "No"}</Typography>
                </Stack>
              </FormControl>

              <FormControl>
                <FormLabel sx={{ mb: 0.5, fontSize: 12 }}>Client Active</FormLabel>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Switch checked={isClientActive} onChange={(e) => setIsClientActive(e.target.checked)} />
                  <Typography variant="body2">{isClientActive ? "Yes" : "No"}</Typography>
                </Stack>
              </FormControl>
            </Stack> */}

            {errorMsg ? (
              <Typography variant="body2" color="error">
                {errorMsg}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="secondary" onClick={() => setModalOpen(false)} disabled={submitting}>
            Cancel
          </MDButton>
          <MDButton
            variant="contained"
            color="info"
            onClick={handleSave}
            disabled={submitting || !valid}
          >
            {submitting ? (mode === "add" ? "Saving…" : "Updating…") : mode === "add" ? "Save" : "Update"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ClientConfigurations;
